/**
 * H5 外观主题：与 locale 同源自 AppBridge.getEnvironment，浏览器直开时用 URL / 系统偏好兜底。
 * 优先级：URL ?theme= > Bridge(AppBridge.getEnvironment.theme) > localStorage > 系统偏好。
 */
import { getEnvironment } from '@/utils/feizhou-bridge'
import { normalizeTheme, resolveTheme, themeFromUrl } from './theme-core.js'

const STORAGE_KEY = 'feizhou:theme'
const EVENT_NAME = 'feizhou:theme'

let current = 'light'

function fromUrl() {
  return themeFromUrl(window.location.href)
}

function fromStorage() {
  try {
    return normalizeTheme(localStorage.getItem(STORAGE_KEY))
  } catch {
    return ''
  }
}

function fromSystem() {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function isDark() {
  return current === 'dark'
}

export function setTheme(theme, { persist = true } = {}) {
  const next = normalizeTheme(theme) || 'light'
  const changed = next !== current
  current = next

  const root = document.documentElement
  root.setAttribute('data-theme', next)
  root.classList.toggle('van-theme-dark', next === 'dark') // Vant 4 暗色皮肤
  root.style.colorScheme = next
  root.style.background = next === 'dark' ? '#0f172a' : '#f5f6fa'

  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* 隐私模式写入失败可忽略 */
    }
  }
  if (changed) window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }))
}

/** 订阅主题变化，返回取消订阅函数（Ant Design Vue 的 theme 需响应式跟随）。 */
export function onThemeChange(handler) {
  const listener = (e) => handler(e.detail)
  window.addEventListener(EVENT_NAME, listener)
  return () => window.removeEventListener(EVENT_NAME, listener)
}

/** 启动时调用一次；首屏用 index.html 内联脚本算好的值，App 内再按 Bridge 对齐。 */
export function initTheme() {
  const preset = window.__FEIZHOU_THEME__ || {}
  const url = fromUrl()
  setTheme(
    resolveTheme({
      urlTheme: url,
      injectedTheme: preset.mode,
      storedTheme: fromStorage(),
      systemTheme: fromSystem(),
    }),
    { persist: !!url },
  )

  getEnvironment({ timeoutMs: 1500 })
    .then((env) => {
      const theme = normalizeTheme(env.theme)
      if (theme && !url) setTheme(theme)
    })
    .catch(() => {
      /* 无 Bridge：保留本地解析结果 */
    })
}
